import { useEffect, useState } from 'react';
import { Check, Vault } from 'lucide-react';

import { SALES_FIELDS } from '@/data/settings';
import { formatKop } from '@/lib/format';
import { bulkUpsertSettings, getSalesSafe, type BulkSettingsEntry, type SalesSafeSummary } from '@/lib/operatorApi';

/**
 * Сейф отдела продаж прямо на странице «Менеджеры»: какой процент с каждой
 * оплаты подписки туда уходит, с какой даты считать, и что в нём сейчас.
 * Ключи те же, что в «Настройках → Отдел продаж».
 */
export default function SalesSafeCard() {
  const [data, setData] = useState<SalesSafeSummary | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [percent, setPercent] = useState('');
  const [start, setStart] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getSalesSafe()
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setPercent(String(d.percent));
        setStart(d.startDate);
        setLoadError(null);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Не удалось загрузить сейф');
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  useEffect(() => {
    if (!saved) return;
    const t = window.setTimeout(() => setSaved(false), 2500);
    return () => window.clearTimeout(t);
  }, [saved]);

  if (loadError) {
    return <div className="card p-4 text-[13px] text-ink/60">Сейф: {loadError}</div>;
  }
  if (!data) return <div className="card p-4 text-[13px] text-ink/50">Сейф отдела продаж: загрузка…</div>;

  const parsedPercent = Number(percent.trim().replace(',', '.'));
  const dirty = percent.trim() !== String(data.percent) || start !== data.startDate;

  const save = async () => {
    if (!percent.trim() || !Number.isFinite(parsedPercent) || parsedPercent < 0 || parsedPercent > 100) {
      setError('Процент — число от 0 до 100');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) {
      setError('Укажите дату начала учёта');
      return;
    }
    const entries: BulkSettingsEntry[] = [];
    if (parsedPercent !== data.percent) entries.push({ key: SALES_FIELDS.safePercent.key, value: parsedPercent });
    if (start !== data.startDate) entries.push({ key: SALES_FIELDS.safeStart.key, value: start });
    if (!entries.length) return;
    setBusy(true);
    setError(null);
    try {
      await bulkUpsertSettings('sales', entries);
      setSaved(true);
      setVersion((v) => v + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3 md:px-5">
        <div className="flex items-center gap-2">
          <Vault size={15} className="text-ink/50" />
          <span className="eyebrow">Сейф отдела продаж</span>
        </div>
        <span className="text-[12px] text-ink/50">
          {data.percent.toLocaleString('ru-RU')}% с каждой оплаты подписки · учёт с{' '}
          {new Date(`${data.startDate}T00:00:00`).toLocaleDateString('ru-RU')}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-px bg-line md:grid-cols-5">
        <Kpi
          label="Остаток"
          value={formatKop(data.balanceKopecks)}
          strong
          tone={data.balanceKopecks < 0 ? 'red' : undefined}
          className="col-span-2 md:col-span-1"
        />
        <Kpi label="Пришло в этом месяце" value={formatKop(data.thisMonthInKopecks)} />
        <Kpi label="Пришло с даты учёта" value={formatKop(data.inflowKopecks)} />
        <Kpi label="Расходы из сейфа" value={formatKop(data.expensesKopecks)} />
        <Kpi label="Выплаты менеджерам" value={formatKop(data.payoutsKopecks)} />
      </div>

      <div className="flex flex-col gap-3 border-t border-line px-4 py-4 md:flex-row md:items-end md:px-5">
        <label className="block">
          <span className="mb-1 block text-[12px] text-ink/60">В сейф с каждой оплаты, %</span>
          <input
            className="input md:w-[140px]"
            inputMode="decimal"
            value={percent}
            onChange={(e) => {
              setPercent(e.target.value);
              setError(null);
            }}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[12px] text-ink/60">Учитывать оплаты с</span>
          <input
            className="input md:w-[180px]"
            type="date"
            value={start}
            onChange={(e) => {
              if (!e.target.value) return;
              setStart(e.target.value);
              setError(null);
            }}
          />
        </label>
        <button type="button" className="btn-primary md:mb-px" disabled={!dirty || busy} onClick={() => void save()}>
          {saved ? <Check size={14} /> : null}
          {busy ? 'Сохраняем…' : saved ? 'Сохранено' : 'Сохранить'}
        </button>
        {dirty ? (
          <button
            type="button"
            className="btn-ghost md:mb-px"
            onClick={() => {
              setPercent(String(data.percent));
              setStart(data.startDate);
              setError(null);
            }}
          >
            Отменить
          </button>
        ) : null}
      </div>
      <div className="px-4 pb-4 text-[12px] text-ink/50 md:px-5">
        {error ? <span className="text-red-600">{error}</span> : null}
        {error ? ' · ' : ''}
        Со всех оплат подписки компании. Новый процент — для следующих оплат, прошлые не пересчитываются. Дату назад —
        доли за прошлые оплаты догонятся в течение часа. Остаток = поступления − расходы «из сейфа» − выплаты.
      </div>
    </section>
  );
}

function Kpi({
  label,
  value,
  strong,
  tone,
  className,
}: {
  label: string;
  value: string;
  strong?: boolean;
  tone?: 'red';
  className?: string;
}) {
  return (
    <div className={'bg-white px-4 py-3 md:px-5 ' + (className ?? '')}>
      <div className="text-[11px] uppercase tracking-[0.1em] text-ink/45">{label}</div>
      <div
        className={
          'mt-1 tabular-nums ' +
          (strong ? 'text-[20px] font-medium ' : 'text-[15px] ') +
          (tone === 'red' ? 'text-red-600' : 'text-ink')
        }
      >
        {value}
      </div>
    </div>
  );
}
